// Weekdoel, reeks en weekterugblik. Alles wordt afgeleid uit het logboek (geen eigen opslag van voortgang),
// zodat er niets verloren gaat als logs worden aangepast of verwijderd. Pure functies, `now` is injecteerbaar.
// Een week loopt van maandag t/m zondag in lokale tijd.
import { logCategory } from './logs';
import { toDateKey } from './playtime';
import type { LogEntry, MainCategory } from './types';

export interface WeeklyGoal {
  minutes: number;
  onlyOutdoor: boolean;
}

export interface GoalPreset {
  /** Korte knoptekst. */
  label: string;
  /** null = geen doel. */
  minutes: number | null;
}

export const GOAL_PRESETS: readonly GoalPreset[] = [
  { label: 'Geen doel', minutes: null },
  { label: '2 u per week', minutes: 120 },
  { label: '4 u per week', minutes: 240 },
  { label: '6 u per week', minutes: 360 },
];

const MAX_GOAL_MINUTES = 7 * 24 * 60;
/** Bovengrens per sessie, gelijk aan de rest van de app (alles daarboven is corrupte data). */
const MAX_MINUTES_PER_ENTRY = 24 * 60;

/** Veilig parsen van het bewaarde doel (string uit localStorage of al geparste waarde). */
export function parseWeeklyGoal(raw: unknown): WeeklyGoal | null {
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const src = data as Record<string, unknown>;
  const minutes = typeof src.minutes === 'number' ? Math.round(src.minutes) : NaN;
  if (!Number.isFinite(minutes) || minutes < 1 || minutes > MAX_GOAL_MINUTES) return null;
  return { minutes, onlyOutdoor: src.onlyOutdoor === true };
}

/** Een geldige logregel met voorberekende velden. */
export interface PlayEntry {
  at: Date;
  minutes: number;
  outdoor: boolean;
  /** Hoofdletterongevoelige activiteitsnaam. */
  name: string;
  displayName: string;
  category: MainCategory;
}

/** Geldige logs als genormaliseerde beurten, oudste eerst. Ongeldige items vallen weg. */
export function toPlayEntries(logs: readonly LogEntry[]): PlayEntry[] {
  const out: PlayEntry[] = [];
  for (const log of Array.isArray(logs) ? logs : []) {
    if (!log || typeof log !== 'object') continue;
    if (typeof log.moment !== 'string' && typeof log.moment !== 'number') continue;
    const at = new Date(log.moment);
    if (Number.isNaN(at.getTime())) continue;
    const raw = typeof log.duurMinuten === 'number' ? log.duurMinuten : Number(log.duurMinuten);
    if (!Number.isFinite(raw) || raw <= 0) continue;
    const minutes = Math.min(Math.round(raw), MAX_MINUTES_PER_ENTRY);
    const displayName = (typeof log.naam === 'string' ? log.naam.trim() : '') || 'Missie';
    out.push({
      at,
      minutes,
      outdoor: typeof log.type === 'string' && log.type.trim().toLowerCase() === 'buiten',
      name: displayName.toLowerCase(),
      displayName,
      category: logCategory(log),
    });
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** Maandag 00:00 lokaal van de week waarin `d` valt. */
export function startOfWeek(d: Date): Date {
  const back = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
}

/** [start, end) van de week van `now`: maandag 00:00 tot volgende maandag 00:00 (lokaal, DST-veilig). */
export function weekRange(now: Date): { start: Date; end: Date } {
  const start = startOfWeek(now);
  return { start, end: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7) };
}

function inRange(e: PlayEntry, start: Date, end: Date): boolean {
  const t = e.at.getTime();
  return t >= start.getTime() && t < end.getTime();
}

export interface WeekProgress {
  /** Minuten die meetellen voor het doel (bij "alleen buiten" alleen buiten). */
  minutes: number;
  goalMinutes: number;
  /** 0 zonder doel; kan boven 1 komen als het doel ruim gehaald is. */
  fraction: number;
  /** Dagen deze week met minstens één missie. */
  daysActive: number;
  remainingMinutes: number;
  reached: boolean;
}

export function weekProgress(logs: readonly LogEntry[], goal: WeeklyGoal | null, now: Date): WeekProgress {
  const { start, end } = weekRange(now);
  const week = toPlayEntries(logs).filter((e) => inRange(e, start, end));
  const counted = goal?.onlyOutdoor ? week.filter((e) => e.outdoor) : week;
  const minutes = counted.reduce((sum, e) => sum + e.minutes, 0);
  const goalMinutes = goal ? goal.minutes : 0;
  const days = new Set(week.map((e) => toDateKey(e.at)));
  return {
    minutes,
    goalMinutes,
    fraction: goalMinutes > 0 ? minutes / goalMinutes : 0,
    daysActive: days.size,
    remainingMinutes: Math.max(0, goalMinutes - minutes),
    reached: goalMinutes > 0 && minutes >= goalMinutes,
  };
}

/** Opeenvolgende dagen met minstens één missie, eindigend vandaag of gisteren (vandaag is nog niet voorbij). */
export function currentStreak(logs: readonly LogEntry[], now: Date): number {
  const days = new Set(toPlayEntries(logs).map((e) => toDateKey(e.at)));
  let cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (!days.has(toDateKey(cursor))) cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
  let streak = 0;
  while (days.has(toDateKey(cursor))) {
    streak += 1;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
  }
  return streak;
}

export interface WeekRecap {
  /** Maandag van de teruggeblikte week. */
  weekStart: Date;
  totalMinutes: number;
  missions: number;
  /** Aandeel buiten, 0..1 (naar minuten). */
  outdoorShare: number;
  /** Meest gedane activiteit (bij gelijkspel: meeste minuten), of null. */
  topActivity: string | null;
  bestDay: { dateKey: string; date: Date; minutes: number } | null;
  /** Minuten verschil met de week ervoor (positief = meer). */
  deltaVsPrevious: number;
  previousMinutes: number;
  previousMissions: number;
}

function summarizeWeek(entries: PlayEntry[], start: Date, end: Date) {
  const week = entries.filter((e) => inRange(e, start, end));
  const totalMinutes = week.reduce((s, e) => s + e.minutes, 0);
  const outdoorMinutes = week.filter((e) => e.outdoor).reduce((s, e) => s + e.minutes, 0);
  const byName = new Map<string, { name: string; count: number; minutes: number }>();
  const byDay = new Map<string, { date: Date; minutes: number }>();
  for (const e of week) {
    const n = byName.get(e.name) ?? { name: e.displayName, count: 0, minutes: 0 };
    n.count += 1;
    n.minutes += e.minutes;
    byName.set(e.name, n);
    const key = toDateKey(e.at);
    const d = byDay.get(key) ?? { date: new Date(e.at.getFullYear(), e.at.getMonth(), e.at.getDate()), minutes: 0 };
    d.minutes += e.minutes;
    byDay.set(key, d);
  }
  const top = [...byName.values()].sort((a, b) => b.count - a.count || b.minutes - a.minutes || a.name.localeCompare(b.name))[0];
  const best = [...byDay.entries()].sort((a, b) => b[1].minutes - a[1].minutes || a[0].localeCompare(b[0]))[0];
  return {
    missions: week.length,
    totalMinutes,
    outdoorShare: totalMinutes > 0 ? outdoorMinutes / totalMinutes : 0,
    topActivity: top ? top.name : null,
    bestDay: best ? { dateKey: best[0], date: best[1].date, minutes: best[1].minutes } : null,
  };
}

/** Terugblik op de laatste volledige week (maandag t/m zondag vóór deze week) naast de week ervoor. */
export function weekRecap(logs: readonly LogEntry[], now: Date): WeekRecap {
  const entries = toPlayEntries(logs);
  const thisStart = startOfWeek(now);
  const lastStart = new Date(thisStart.getFullYear(), thisStart.getMonth(), thisStart.getDate() - 7);
  const prevStart = new Date(thisStart.getFullYear(), thisStart.getMonth(), thisStart.getDate() - 14);
  const last = summarizeWeek(entries, lastStart, thisStart);
  const prev = summarizeWeek(entries, prevStart, lastStart);
  return {
    weekStart: lastStart,
    ...last,
    deltaVsPrevious: last.totalMinutes - prev.totalMinutes,
    previousMinutes: prev.totalMinutes,
    previousMissions: prev.missions,
  };
}

/** "45 min", "1 u", "3 u 20 m". */
export function formatHoursMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} u` : `${h} u ${rest} m`;
}

const DAY_LONG = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];

/** Rustige zin over de week; geen verwijt bij minder. */
export function recapHeadline(r: WeekRecap): string {
  if (r.missions === 0) return 'Vorige week was het rustig.';
  const base = `Vorige week speelde je ${formatHoursMinutes(r.totalMinutes)}`;
  if (r.previousMinutes === 0) return `${base}.`;
  if (r.deltaVsPrevious > 0) return `${base}, ${formatHoursMinutes(r.deltaVsPrevious)} meer dan de week ervoor.`;
  if (r.deltaVsPrevious < 0) return `${base}. Rustiger weekje, ook goed.`;
  return `${base}, net zoveel als de week ervoor.`;
}

/** Losse feitjes voor onder de kop (alleen wat er te vertellen valt). */
export function recapFacts(r: WeekRecap): string[] {
  if (r.missions === 0) return [];
  const facts = [`${r.missions} ${r.missions === 1 ? 'missie' : 'missies'} voltooid`];
  const outdoor = Math.round(r.outdoorShare * 100);
  facts.push(outdoor === 0 ? 'Alles binnen gespeeld' : `${outdoor}% van je speeltijd was buiten`);
  if (r.topActivity) facts.push(`Meest gedaan: ${r.topActivity}`);
  if (r.bestDay) facts.push(`Beste dag: ${DAY_LONG[r.bestDay.date.getDay()]} (${formatHoursMinutes(r.bestDay.minutes)})`);
  return facts;
}
